import type { paths } from "@/api-schema";
import { AuditLogEntityName, PathsV1RelocationOrderUuidGetParametersQueryExtend } from "@/api-schema-enums";
import { useAbility } from "@/casl-provider";
import { useRelocationOrderFlowCancelMutation } from "@/common/api/relocation-order-flow/use-relocation-order-flow-cancel.mutation";
import { useRelocationOrderByUuidQuery } from "@/common/api/relocation-order/use-relocation-order-by-uuid.query";
import { useRelocationOrderDeleteMutation } from "@/common/api/relocation-order/use-relocation-order-delete.mutation";
import { withKeys } from "@/common/helpers/list-keys";
import { toastMutationStatus } from "@/common/helpers/toast-mutation-status";
import { canCancelRelocationOrder } from "@/common/hooks/relocation-order/_helpers/can-cancel-relocation-order";
import { sortRelocationOrderActions } from "@/common/hooks/relocation-order/_helpers/sort-relocation-order-actions";
import { useRelocationOrderExternalActions } from "@/common/hooks/relocation-order/use-relocation-order-external-actions";
import { useRelocationOrderInternalActions } from "@/common/hooks/relocation-order/use-relocation-order-internal-actions";
import { AuditLogCard } from "@/components/audit-log/audit-log-card";
import { BrandLogo, BrandLogoVariant } from "@/components/brand-logo/brand-logo";
import { CommandButton } from "@/components/command-palette/command-button";
import { BaseConfirmDialog } from "@/components/confirm-dialog/base-confirm-dialog";
import { ConfirmationType } from "@/components/confirm-dialog/_types";
import { DeleteConfirmDialog } from "@/components/confirm-dialog/predefined/delete-confirm-dialog";
import { ErrorPage } from "@/components/error-page/error-page";
import { CenteredLayout } from "@/components/layouts/centered-layout";
import { ButtonColorVariant, ButtonSize, ButtonVariant } from "@/components/ui/button";
import { RowAction, RowActionVariant } from "@/components/ui/table-row-actions";
import { UuidExistenceWrapper } from "@/components/uuid-existence-wrapper/uuid-existence-wrapper";
import { VehicleInfoCard } from "@/components/vehicle-info-card/vehicle-info-card";
import { BanIcon, PencilIcon, TrashIcon } from "lucide-react";
import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useParams } from "wouter";
import { RelocationOrderDetailInfoCard } from "./_components/relocation-order-detail-info-card";
import { RelocationOrderDetailRouteCard } from "./_components/relocation-order-detail-route-card/relocation-order-detail-route-card";
import { RelocationOrderDetailSkeleton } from "./_components/relocation-order-detail-skeleton";
import { useRelocationOrderDetailAccess } from "./_hooks/use-relocation-order-detail-access";

const RELOCATION_ORDER_DETAIL_EXTENDS = [
  PathsV1RelocationOrderUuidGetParametersQueryExtend.Vehicle,
  PathsV1RelocationOrderUuidGetParametersQueryExtend.Vehicle_ModelGeneration,
  PathsV1RelocationOrderUuidGetParametersQueryExtend.Vehicle_ModelGeneration_ModelFamily,
  PathsV1RelocationOrderUuidGetParametersQueryExtend.Vehicle_ModelGeneration_ModelFamily_Brand,
  PathsV1RelocationOrderUuidGetParametersQueryExtend.Vehicle_OwningBranch,
  PathsV1RelocationOrderUuidGetParametersQueryExtend.Vehicle_OwningBranch_Company,
  PathsV1RelocationOrderUuidGetParametersQueryExtend.Vehicle_CurrentBranch,
  PathsV1RelocationOrderUuidGetParametersQueryExtend.Vehicle_VehicleBlock,
  PathsV1RelocationOrderUuidGetParametersQueryExtend.CurrentBranch,
  PathsV1RelocationOrderUuidGetParametersQueryExtend.CurrentBranch_Address,
  PathsV1RelocationOrderUuidGetParametersQueryExtend.CurrentAddress,
  PathsV1RelocationOrderUuidGetParametersQueryExtend.OriginBranch,
  PathsV1RelocationOrderUuidGetParametersQueryExtend.OriginBranch_Company,
  PathsV1RelocationOrderUuidGetParametersQueryExtend.OriginBranch_Address,
  PathsV1RelocationOrderUuidGetParametersQueryExtend.OriginAddress,
  PathsV1RelocationOrderUuidGetParametersQueryExtend.DestinationBranch,
  PathsV1RelocationOrderUuidGetParametersQueryExtend.DestinationBranch_Company,
  PathsV1RelocationOrderUuidGetParametersQueryExtend.DestinationBranch_Address,
  PathsV1RelocationOrderUuidGetParametersQueryExtend.DestinationAddress,
  PathsV1RelocationOrderUuidGetParametersQueryExtend.PickedUpBy,
  PathsV1RelocationOrderUuidGetParametersQueryExtend.TransportCompany,
  PathsV1RelocationOrderUuidGetParametersQueryExtend.OrderedBy,
];

export function RelocationOrderDetailView() {
  const { t } = useTranslation(["fit", "common"]);
  const params = useParams<{ uuid: string }>();
  const relocationOrderUuid = params.uuid;
  const { overviewPath } = useRelocationOrderDetailAccess();

  return (
    <UuidExistenceWrapper<
      paths["/v1/relocation-order/{uuid}"]["get"]["parameters"]["query"],
      paths["/v1/relocation-order/{uuid}"]["get"]["responses"][200]["content"]["application/json"]
    >
      uuid={relocationOrderUuid}
      query={useRelocationOrderByUuidQuery}
      notFoundProps={{
        title: t("fit:RelocationOrderNotFound"),
        message: t("fit:RequestedOrderDoesNotExist"),
        description: t("fit:PleaseCheckOrderIdOrContactSupport"),
        backTo: overviewPath,
        backToLabel: t("fit:BackToOrderOverview"),
      }}
    >
      <RelocationOrderDetailContent uuid={relocationOrderUuid} />
    </UuidExistenceWrapper>
  );
}

interface RelocationOrderDetailContentProps {
  uuid: string;
}

function RelocationOrderDetailContent({ uuid }: RelocationOrderDetailContentProps) {
  const { t } = useTranslation(["fit", "common"]);
  const [_, navigate] = useLocation();
  const ability = useAbility();
  const { canManage, canUpdate, showInternalFlowActions, showExternalFlowActions, overviewPath } =
    useRelocationOrderDetailAccess();
  const { mutateAsync: deleteRelocationOrderMutate } = useRelocationOrderDeleteMutation();
  const { mutateAsync: cancelRelocationOrderMutate, isPending: isCancellingRelocationOrder } =
    useRelocationOrderFlowCancelMutation();
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);

  const handleEdit = useCallback(() => {
    navigate(`~/fit/relocation-order/${uuid}/update`);
  }, [navigate, uuid]);

  const handleDelete = useCallback(() => {
    setConfirmDialogOpen(true);
  }, []);

  const handleCancelDelete = useCallback(() => {
    setConfirmDialogOpen(false);
  }, []);

  const handleConfirmDelete = useCallback(async () => {
    await toastMutationStatus({
      mutationFn: () => deleteRelocationOrderMutate({ uuid }),
      successMessage: t("common:ItemHasBeenDeletedSuccessfully"),
      errorMessage: t("common:ErrorDeletingItem"),
      onSuccess: () => {
        setConfirmDialogOpen(false);
        navigate(overviewPath);
      },
    });
  }, [uuid, deleteRelocationOrderMutate, t, navigate, overviewPath]);

  const handleConfirmCancel = useCallback(async () => {
    setCancelDialogOpen(false);
    await toastMutationStatus({
      mutationFn: () => cancelRelocationOrderMutate({ uuid }),
      successMessage: t("fit:RelocationOrderCancelled"),
      errorMessage: t("fit:ErrorCancellingRelocationOrder"),
    });
  }, [uuid, cancelRelocationOrderMutate, t]);

  const {
    data: relocationOrder,
    isLoading,
    isError,
  } = useRelocationOrderByUuidQuery(uuid, {
    parameters: { extend: RELOCATION_ORDER_DETAIL_EXTENDS },
  });

  const { getActionsForOrder: getInternalActionsForOrder, dialogs: internalDialogs } =
    useRelocationOrderInternalActions({
      enabled: showInternalFlowActions,
    });
  const { getActionsForOrder: getExternalActionsForOrder } = useRelocationOrderExternalActions();

  const actions = useMemo(() => {
    if (!relocationOrder) return [];

    const orderActions: RowAction[] = [
      ...(showInternalFlowActions ? getInternalActionsForOrder(relocationOrder) : []),
      ...(showExternalFlowActions ? getExternalActionsForOrder(relocationOrder) : []),
    ];

    if (canUpdate) {
      orderActions.push({
        label: t("fit:AdminUpdate"),
        icon: <PencilIcon />,
        onClick: handleEdit,
      });
    }

    if (canCancelRelocationOrder(ability, relocationOrder)) {
      orderActions.push({
        label: t("fit:CancelRelocationOrder"),
        icon: <BanIcon />,
        variant: RowActionVariant.Error,
        disabled: isCancellingRelocationOrder,
        onClick: () => setCancelDialogOpen(true),
      });
    }

    return sortRelocationOrderActions(orderActions).toReversed();
  }, [
    relocationOrder,
    getInternalActionsForOrder,
    getExternalActionsForOrder,
    showInternalFlowActions,
    showExternalFlowActions,
    canUpdate,
    ability,
    isCancellingRelocationOrder,
    handleEdit,
    t,
  ]);

  if (isLoading) {
    return <RelocationOrderDetailSkeleton />;
  }

  if (isError || !relocationOrder) {
    return (
      <ErrorPage
        title={t("fit:RelocationOrderNotFound")}
        message={t("fit:RequestedOrderDoesNotExist")}
        description={t("fit:PleaseCheckOrderIdOrContactSupport")}
        backTo={overviewPath}
        backToLabel={t("fit:BackToOrderOverview")}
      />
    );
  }

  const vehicle = relocationOrder.vehicle;
  const brand = vehicle?.modelGeneration?.modelFamily?.brand;

  const vehicleName = [brand?.name, vehicle?.modelGeneration?.name].filter(Boolean).join(" ") || t("common:Vehicle");

  const titleNode = (
    <div className="flex flex-wrap items-center justify-between gap-4">
      <h1 className="flex items-center gap-2 text-2xl font-semibold tracking-tight sm:text-3xl">
        {brand && <BrandLogo variant={BrandLogoVariant.MEDIUM} brand={brand} />}
        <span>{vehicleName}</span>
      </h1>
      <div className="flex flex-row items-center gap-2">
        {canManage && (
          <CommandButton
            commandId="relocation-order-delete"
            label={t("fit:AdminDelete")}
            icon={<TrashIcon />}
            size={ButtonSize.Sm}
            variant={ButtonVariant.Filled}
            colorVariant={ButtonColorVariant.Error}
            onClick={handleDelete}
          />
        )}
        {withKeys(actions, (action, index) => `${action.label}-${index}`).map(({ key, item: action }) => (
          <CommandButton
            key={key}
            commandId={`relocation-order-${key}`}
            label={action.label}
            icon={action.icon}
            variant={ButtonVariant.Outlined}
            colorVariant={
              (action.variant as string) === ButtonColorVariant.Error
                ? ButtonColorVariant.Error
                : ButtonColorVariant.Secondary
            }
            size={ButtonSize.Sm}
            disabled={action.disabled}
            onClick={() => action.onClick?.()}
          />
        ))}
      </div>
    </div>
  );

  return (
    <CenteredLayout className="md:max-w-5xl" title={titleNode}>
      <div className="flex flex-col gap-6">
        <VehicleInfoCard vehicle={vehicle} />
        <RelocationOrderDetailInfoCard relocationOrder={relocationOrder} />
        <RelocationOrderDetailRouteCard relocationOrder={relocationOrder} />
        <AuditLogCard entityName={AuditLogEntityName.RelocationOrder} uuid={uuid} />
      </div>

      <DeleteConfirmDialog
        open={confirmDialogOpen}
        label={t("fit:RelocationOrder")}
        title={t("fit:DeleteRelocationOrder", { vin: vehicle?.vin })}
        name={vehicle?.vin ?? ""}
        onCancel={handleCancelDelete}
        onConfirm={handleConfirmDelete}
        requireConfirmation={true}
        confirmationType={ConfirmationType.Name}
        showIrreversibleWarning={true}
      />

      <BaseConfirmDialog
        open={cancelDialogOpen}
        title={t("fit:CancelRelocationOrder")}
        description={t("fit:CancelRelocationOrderDescription")}
        labelCancel={t("common:Back")}
        labelConfirm={t("fit:CancelRelocationOrder")}
        confirmColorVariant={ButtonColorVariant.Error}
        onCancel={() => setCancelDialogOpen(false)}
        onConfirm={handleConfirmCancel}
      />

      {showInternalFlowActions && internalDialogs}
    </CenteredLayout>
  );
}
